
import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useState } from 'react';

import CorporationUtils from '@/common/utils/CorporationUtils';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { getListOnDemandAtomByPanelType } from '@/state/atom/translationAtom';
import {
    setListTicketOnDemandActiveView,
    requestListTicketOnDemandTranslation
} from '@/component/multiLanguage/handlers/ListTicketOnDemandHandler';
import { trI18n } from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import TranslationConstant from '@/common/constant/TranslationConstant';

import './ListTicketOnDemandControl.css';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';

const { ON_DEMAND_TRANSLATION_STATUS, ON_DEMAND_TRANSLATION_VIEW } = TranslationConstant;

ListTicketOnDemandControl.propTypes = {
    panelType: PropTypes.string,
    viewKey: PropTypes.string,
    className: PropTypes.string,
    totalTickets: PropTypes.number,
};

/**
 * 「翻訳版」toggle for the ticket list toolbar (MLT-001-F4; 3 list screens: helpdesk /
 * Q&A-customer / knowledge). Whole-list toggle: ON shows every record's translation,
 * OFF shows originals. State lives in the per-screen list atom (resolved from panelType)
 * keyed by the current viewKey, so every view keeps its own toggle; this control reflects
 * the view-level toggle (toggleStatus / activeView) and flips the view.
 *
 * Turning ON: if the view was already translated this session, just show the cached
 * translation (no re-translate, BR-6); otherwise call the on-demand translation. OFF
 * only hides the translation (kept in session). The toggle is disabled while LOADING.
 */
export default function ListTicketOnDemandControl({ panelType, viewKey, className = '', totalTickets }) {
    useTranslationSubscription();
    const [isOpenModalReloadPage, setIsOpenModalReloadPage] = useState(false);
    const corporationDetail = useRecoilValue(corporationAtom);
    // Pick the per-screen atom from panelType (stable for a given toolbar instance).
    const listOnDemandAtom = getListOnDemandAtomByPanelType(panelType);
    const [listTicketOnDemand, setListTicketOnDemand] = useRecoilState(listOnDemandAtom);

    const listRecord = viewKey ? listTicketOnDemand[viewKey] : null;
    const toggleStatus = listRecord?.toggleStatus || ON_DEMAND_TRANSLATION_STATUS.IDLE;
    const activeView = listRecord?.activeView || ON_DEMAND_TRANSLATION_VIEW.ORIGINAL;

    // Hide entirely when the multi-language feature is off for this organization (AC-11).
    if (!CorporationUtils.isEnableSettingMultiLanguage(corporationDetail)) return null;

    const isLoading = toggleStatus === ON_DEMAND_TRANSLATION_STATUS.LOADING;
    const isShowingTranslation = activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION;
    const isDisabled = isLoading || Number(totalTickets) === 0;

    const handleToggleOnDemand = () => {
        if (isDisabled) return;

        // Turning OFF -> show originals in place (translations are kept, BR-6).
        if (isShowingTranslation) {
            setListTicketOnDemandActiveView(
                setListTicketOnDemand,
                viewKey,
                ON_DEMAND_TRANSLATION_VIEW.ORIGINAL
            );
            return;
        }

        // Turning ON: already translated this session (toggleStatus TRANSLATED) → just
        // show the cached translation, do NOT re-translate (BR-6).
        if (toggleStatus === ON_DEMAND_TRANSLATION_STATUS.TRANSLATED) {
            setListTicketOnDemandActiveView(
                setListTicketOnDemand,
                viewKey,
                ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
            );
            return;
        }

        // First ON (IDLE / ERROR): the toggle follows the user right away (CR-303134 BR-0) - rows
        // keep their original until their translation arrives, and a failure no longer turns the
        // toggle back off. Records with no pending translation are skipped, and new records
        // appearing later are picked up by the auto-translate effect.
        setListTicketOnDemandActiveView(
            setListTicketOnDemand,
            viewKey,
            ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
        );
        requestListTicketOnDemandTranslation({
            panelType,
            viewKey,
            listRecord,
            setListTicketOnDemand,
            setIsOpenModalReloadPage
        });
    };

    const toggleId = `list-ticket-ondemand-translation-toggle-${panelType || 'list'}-${viewKey || 'view'}`;

    return (
        <div
            className={`list-ticket-ondemand-translation ${className}`.trim()}
            data-cy="list-ticket-ondemand-translation"
        >
            <span className="list-ticket-ondemand-translation-label">
                {trI18n('CM-ONDEMAND-TRANSLATION-TOGGLE-LABEL')}
            </span>

            <div className="switch-check">
                <input
                    name="listTicketOnDemandTranslation"
                    id={toggleId}
                    type="checkbox"
                    checked={isShowingTranslation}
                    disabled={isDisabled}
                    onChange={handleToggleOnDemand}
                    data-cy="list-ticket-ondemand-translation-toggle"
                />
                <label htmlFor={toggleId}></label>
            </div>

            {isLoading && (
                <Tooltip title={trI18n('CM-ONDEMAND-TRANSLATION-LOADING-TOOLTIP')} arrow placement="right-end">
                    <img
                        className="option-icon icon-loading"
                        src={`/assets/image/icon-loading.svg`}
                        alt="icon-loading"
                        data-cy="list-ticket-ondemand-translation-loading"
                    />
                </Tooltip>
            )}

            <DataChangePleaseReloadModal
                isOpen={isOpenModalReloadPage}
                setIsOpen={setIsOpenModalReloadPage}
            />
        </div>
    );
}
